import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "sonner";
import {
  FaCreditCard,
  FaLock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaArrowLeft,
  FaEnvelope,
} from "react-icons/fa";

import { ReactComponent as PaymentAccept } from "../../assets/icons/banka.svg";
import MainContainer from "../../components/MainContainer/MainContainer";
import SEO from "../../components/SEO/SEO";
import PaymentRedirect from "../../components/Payment/PaymentRedirect";
import {
  Container,
  CheckoutHeader,
  CheckoutContent,
  CheckoutForm,
  SectionTitle,
  FormSection,
  FormRow,
  FormGroup,
  Input,
  SelectFieldWrapper,
  CheckboxContainer,
  PaymentMethodSelector,
  PaymentMethod,
  OrderSummary,
  SummaryHeader,
  OrderItem,
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  PlaceOrderButton,
  ErrorMessage,
  LinkButton,
  EmailVerificationBanner,
  EmailVerificationHeader,
  EmailVerificationText,
  EmailVerificationButton,
  AutoFilledDataBanner,
  LoginPromptBanner,
  BackButton,
  PaymentContainer,
} from "./CheckoutPage.styles";

import { selectUser, selectLoggedIn } from "../../store/slices/auth/slice";
import { resendVerification } from "../../store/slices/auth/asyncThunks";
import { priceFormatter } from "../../utils/utils";
import paymentService from "../../api/services/payment";
import CustomSelect from "../../components/utils/CustomSelect";
import styled from "styled-components";
import { BASE_URL } from "../../api";

const CheckoutPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux selectors
  const user = useSelector(selectUser);
  const isLoggedIn = useSelector(selectLoggedIn);

  // Form state - BEZ POLJA ZA KARTICU (kartica se unosi na Raiffeisen stranici)
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postalCode: "",
    country: "Srbija",
    paymentMethod: "card",
    agreeToTerms: false,
    subscribeNewsletter: false,
  });

  const [errors, setErrors] = useState({});
  const [orderError, setOrderError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [resendingVerification, setResendingVerification] = useState(false);

  // Payment methods
  const paymentMethods = [
    {
      id: "card",
      name: "Kreditna/Debitna Kartica",
      description:
        "Sigurno plaćanje preko Raiffeisen banke (Visa, MasterCard, Maestro)",
      icon: <FaCreditCard />,
    },
  ];

  const onNavigate = (path) => {
    window.open(`/${path}`, "_blank");
  };

  // Check if user is logged in
  useEffect(() => {
    if (isLoggedIn === false && user === null) {
      navigate("/pristup");
      return;
    }
  }, [isLoggedIn, user, navigate]);

  // Local state for checkout data
  const [checkoutData, setCheckoutData] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(true);

  // Load checkout data from localStorage
  useEffect(() => {
    const savedCheckoutData = JSON.parse(
      localStorage.getItem("checkoutData") || "{}",
    );

    if (!savedCheckoutData.items || savedCheckoutData.items.length === 0) {
      navigate("/korpa");
      setTimeout(() => window.scrollTo(0, 0), 300);
      return;
    }

    setCheckoutData(savedCheckoutData);
    setCheckoutLoading(false);
  }, [navigate]);

  // Auto-populate user data
  useEffect(() => {
    if (isLoggedIn && user) {
      setFormData((prev) => ({
        ...prev,
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phone || prev.phone || "",
        address: prev.address || "",
        city: prev.city || "",
        postalCode: prev.postalCode || "",
      }));
    }
  }, [isLoggedIn, user]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle payment method selection
  const handlePaymentMethodChange = (methodId) => {
    setFormData((prev) => ({ ...prev, paymentMethod: methodId }));
  };

  // Validate form - BEZ VALIDACIJE KARTICE
  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = "Ime je obavezno";
    if (!formData.lastName.trim()) newErrors.lastName = "Prezime je obavezno";
    if (!formData.email.trim()) newErrors.email = "Email je obavezan";
    if (!formData.phone.trim()) newErrors.phone = "Telefon je obavezan";
    if (!formData.address.trim()) newErrors.address = "Adresa je obavezna";
    if (!formData.city.trim()) newErrors.city = "Grad je obavezan";
    if (!formData.postalCode.trim())
      newErrors.postalCode = "Poštanski broj je obavezan";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = "Neispravna email adresa";
    }

    const phoneRegex = /^[\d\s+\-()]+$/;
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = "Neispravan format telefona";
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = "Morate prihvatiti uslove korišćenja";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle resend verification
  const handleResendVerification = async () => {
    if (!user?.email) return;

    setResendingVerification(true);
    try {
      await dispatch(resendVerification({ email: user.email })).unwrap();
      toast.success("Verifikacioni email je poslat na vašu adresu.");
    } catch (error) {
      toast.error(error || "Greška pri slanju verifikacionog emaila");
    } finally {
      setResendingVerification(false);
    }
  };

  // Handle form submission - INITIATE PAYMENT
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Zaštita od duplog submita (brzi dupli klik pre re-rendera)
    if (isProcessing) return;

    setOrderError("");

    // Check email verification
    if (!user?.isEmailVerified) {
      toast.error("Morate verifikovati svoj email pre finalizacije porudžbine");
      return;
    }

    if (!validateForm()) {
      toast.error("Molimo popunite sva obavezna polja");
      return;
    }

    setIsProcessing(true);

    try {
      // Prepare order data
      const orderData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        postalCode: formData.postalCode,
        country: formData.country,
        subscribeNewsletter: formData.subscribeNewsletter,
        cartItems: checkoutData.items,
        subtotal: checkoutData.subtotal,
        discount: checkoutData.discount,
        total: checkoutData.total,
        appliedPromo: checkoutData.appliedPromo,
      };

      // Save cart items for potential retry
      localStorage.setItem(
        "lastOrderItems",
        JSON.stringify(checkoutData.items),
      );

      // Initiate payment - this creates order and returns payment form data
      const response = await paymentService.initiatePayment(orderData);

      if (response.success && response.freeOrder) {
        // Free order (100% discount) - skip gateway, redirect to success
        toast.success("Porudžbina završena — besplatan pristup odobren!");
        navigate(`/uspesna-porudzbina?orderNumber=${response.order.orderNumber}`);
      } else if (response.success && response.paymentData) {
        // Set payment data to trigger redirect to gateway
        setPaymentData(response.paymentData);
        toast.success("Preusmeravanje na plaćanje...");
      } else {
        throw new Error(response.message || "Greška pri iniciranju plaćanja");
      }
    } catch (error) {
      console.error("Payment initiation error:", error);
      setOrderError(
        error.message ||
          "Greška prilikom iniciranja plaćanja. Pokušajte ponovo.",
      );
      setIsProcessing(false);
    }
  };

  const handleGoBack = () => {
    navigate("/korpa");
    setTimeout(() => window.scrollTo(0, 0), 300);
  };

  // If payment data received, show redirect component
  if (paymentData) {
    return (
      <PaymentRedirect
        paymentData={paymentData}
        onError={(msg) => {
          setOrderError(msg);
          setPaymentData(null);
          setIsProcessing(false);
        }}
      />
    );
  }

  // Don't render if user is not logged in or no checkout data
  if (!isLoggedIn || checkoutLoading || !checkoutData) {
    return checkoutLoading ? <div>Učitavanje...</div> : null;
  }

  return (
    <MainContainer>
      <SEO title="Plaćanje" path="/placanje" noindex />

      <Container>
        <CheckoutHeader>
          <h1>Finalizuj Porudžbinu</h1>
          <p>
            Popunite vaše podatke. Nakon toga bićete preusmereni na sigurnu
            stranicu Raiffeisen banke za unos podataka kartice.
          </p>
        </CheckoutHeader>

        <CheckoutContent>
          <CheckoutForm onSubmit={handleSubmit}>
            {/* Error Message */}
            {orderError && (
              <ErrorMessage>
                <FaExclamationTriangle className="icon" />
                {orderError}
              </ErrorMessage>
            )}

            {/* Email Verification Warning */}
            {isLoggedIn && user && !user.isEmailVerified && (
              <EmailVerificationBanner>
                <EmailVerificationHeader>
                  <FaExclamationTriangle />
                  <span>Email Nije Verifikovan</span>
                </EmailVerificationHeader>
                <EmailVerificationText>
                  Morate verifikovati svoj email pre finalizacije porudžbine.
                </EmailVerificationText>
                <EmailVerificationButton
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendingVerification}
                >
                  {resendingVerification ? (
                    "Šalje se..."
                  ) : (
                    <>
                      <FaEnvelope />
                      Pošalji Verification Email
                    </>
                  )}
                </EmailVerificationButton>
              </EmailVerificationBanner>
            )}

            {/* Billing Information */}
            <FormSection>
              <SectionTitle>Lični Podaci</SectionTitle>

              {isLoggedIn && user ? (
                <AutoFilledDataBanner>
                  <FaCheckCircle />
                  <span>Vaši podaci su automatski popunjeni.</span>
                </AutoFilledDataBanner>
              ) : (
                <LoginPromptBanner>
                  <FaExclamationTriangle />
                  <span>
                    <LinkButton onClick={() => onNavigate("pristup")}>
                      Prijavite se
                    </LinkButton>{" "}
                    da bi vaši podaci bili automatski popunjeni.
                  </span>
                </LoginPromptBanner>
              )}

              <FormRow $columns="1fr 1fr">
                <FormGroup>
                  <label htmlFor="firstName">
                    Ime <span className="required">*</span>
                  </label>
                  <Input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Vaše ime"
                    disabled={isLoggedIn && user && user.firstName}
                    required
                  />
                  {errors.firstName && (
                    <span style={{ color: "var(--error)", fontSize: "12px" }}>
                      {errors.firstName}
                    </span>
                  )}
                </FormGroup>

                <FormGroup>
                  <label htmlFor="lastName">
                    Prezime <span className="required">*</span>
                  </label>
                  <Input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Vaše prezime"
                    disabled={isLoggedIn && user && user.lastName}
                    required
                  />
                  {errors.lastName && (
                    <span style={{ color: "var(--error)", fontSize: "12px" }}>
                      {errors.lastName}
                    </span>
                  )}
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <label htmlFor="email">
                    Email Adresa <span className="required">*</span>
                  </label>
                  <Input
                    disabled={isLoggedIn}
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="vasa.email@adresa.com"
                    required
                  />
                  {errors.email && (
                    <span style={{ color: "var(--error)", fontSize: "12px" }}>
                      {errors.email}
                    </span>
                  )}
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <label htmlFor="phone">
                    Broj Telefona <span className="required">*</span>
                  </label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+381 69 123 4567"
                    disabled={isLoggedIn && user && user.phone}
                    required
                  />
                  {errors.phone && (
                    <span style={{ color: "var(--error)", fontSize: "12px" }}>
                      {errors.phone}
                    </span>
                  )}
                </FormGroup>
              </FormRow>
            </FormSection>

            {/* Address Information */}
            <FormSection>
              <SectionTitle>Adresa</SectionTitle>

              <FormRow>
                <FormGroup>
                  <label htmlFor="address">
                    Ulica i Broj <span className="required">*</span>
                  </label>
                  <Input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Kralja Petra 15"
                    required
                  />
                  {errors.address && (
                    <span style={{ color: "var(--error)", fontSize: "12px" }}>
                      {errors.address}
                    </span>
                  )}
                </FormGroup>
              </FormRow>

              <FormRow $columns="1fr 1fr 1fr">
                <FormGroup>
                  <label htmlFor="city">
                    Grad <span className="required">*</span>
                  </label>
                  <Input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Beograd"
                    required
                  />
                  {errors.city && (
                    <span style={{ color: "var(--error)", fontSize: "12px" }}>
                      {errors.city}
                    </span>
                  )}
                </FormGroup>

                <FormGroup>
                  <label htmlFor="postalCode">
                    Poštanski Broj <span className="required">*</span>
                  </label>
                  <Input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    placeholder="11000"
                    required
                  />
                  {errors.postalCode && (
                    <span style={{ color: "var(--error)", fontSize: "12px" }}>
                      {errors.postalCode}
                    </span>
                  )}
                </FormGroup>

                <FormGroup>
                  <label>
                    Zemlja <span className="required">*</span>
                  </label>
                  <SelectFieldWrapper>
                    <CustomSelect
                      value={formData.country}
                      onChange={(country) =>
                        setFormData((prev) => ({ ...prev, country }))
                      }
                      options={[
                        { value: "Srbija", label: "Srbija" },
                        { value: "Crna Gora", label: "Crna Gora" },
                        {
                          value: "Bosna i Hercegovina",
                          label: "Bosna i Hercegovina",
                        },
                        { value: "Hrvatska", label: "Hrvatska" },
                        { value: "Slovenija", label: "Slovenija" },
                        { value: "Makedonija", label: "Makedonija" },
                      ]}
                      ariaLabel="Zemlja"
                    />
                  </SelectFieldWrapper>
                </FormGroup>
              </FormRow>
            </FormSection>

            {/* Payment Method */}
            <FormSection>
              <SectionTitle>Način Plaćanja</SectionTitle>

              <PaymentMethodSelector>
                {paymentMethods.map((method) => (
                  <PaymentMethod
                    key={method.id}
                    $selected={formData.paymentMethod === method.id}
                    onClick={() => handlePaymentMethodChange(method.id)}
                  >
                    <div className="method-header">
                      <div className="icon">{method.icon}</div>
                      <div className="info">
                        <h4>{method.name}</h4>
                        <p>{method.description}</p>
                      </div>
                      <div className="radio"></div>
                    </div>
                  </PaymentMethod>
                ))}
              </PaymentMethodSelector>

              {/* Payment info notice */}
              <PaymentNotice>
                <FaLock className="icon" />
                <p>
                  Podaci o kartici se unose na sigurnoj stranici Raiffeisen
                  banke. Mi nikada ne vidimo niti čuvamo podatke vaše kartice.
                </p>
              </PaymentNotice>
            </FormSection>

            {/* Terms and Conditions */}
            <CheckboxContainer>
              <input
                type="checkbox"
                id="agreeToTerms"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleInputChange}
                required
              />
              <label htmlFor="agreeToTerms">
                Prihvatam{" "}
                <span onClick={() => onNavigate("uslovi-koriscenja")}>
                  uslove korišćenja
                </span>{" "}
                i{" "}
                <span onClick={() => onNavigate("politika-privatnosti")}>
                  politiku privatnosti
                </span>{" "}
                <span className="required">*</span>
              </label>
              {errors.agreeToTerms && (
                <span style={{ color: "var(--error)", fontSize: "12px" }}>
                  {errors.agreeToTerms}
                </span>
              )}
            </CheckboxContainer>

            {/* translate="no": prevodilac stranice (Google Translate) menja
                tekstualne čvorove, a ovom dugmetu se sadržaj menja pri kliku —
                bez ovoga React može da se sruši baš pri pokretanju plaćanja */}
            <PlaceOrderButton
              type="submit"
              translate="no"
              disabled={isProcessing || !user?.isEmailVerified}
              className={isProcessing ? "processing" : ""}
            >
              {isProcessing ? (
                <>
                  <l-line-spinner size="24" stroke="2" speed="1" color="#fff" />
                  Obrađuje se...
                </>
              ) : (
                <>
                  <FaCreditCard />
                  Nastavi na Plaćanje
                </>
              )}
            </PlaceOrderButton>

            <BackButton type="button" onClick={handleGoBack}>
              <FaArrowLeft />
              Nazad u Korpu
            </BackButton>
          </CheckoutForm>

          {/* Order Summary */}
          <OrderSummary>
            <SummaryHeader>Pregled Porudžbine</SummaryHeader>

            {checkoutData.items.map((item) => (
              <OrderItem key={item.courseId}>
                <img
                  className="item-image"
                  src={`${BASE_URL}/uploads/courses-thumbnails/${item.thumbnailImage}`}
                  alt={`${item.title} thumbnail`}
                />
                <div className="item-details">
                  <div className="item-name">{item.title}</div>
                  <div className="item-description">{item.description}</div>
                  <div className="item-price">
                    {priceFormatter(item.price)} {item.currency}
                  </div>
                </div>
              </OrderItem>
            ))}

            <SummaryRow>
              <SummaryLabel>Međuzbir:</SummaryLabel>
              <SummaryValue>
                {priceFormatter(checkoutData.subtotal)} RSD
              </SummaryValue>
            </SummaryRow>

            {checkoutData.appliedPromo && (
              <SummaryRow>
                <SummaryLabel>
                  Popust ({checkoutData.appliedPromo.name}):
                </SummaryLabel>
                <SummaryValue className="discount">
                  -{priceFormatter(checkoutData.discount)} RSD
                </SummaryValue>
              </SummaryRow>
            )}

            <SummaryRow className="total-row">
              <SummaryLabel className="total">UKUPNO:</SummaryLabel>
              <SummaryValue className="total">
                {priceFormatter(checkoutData.total)} RSD
              </SummaryValue>
            </SummaryRow>
            <PaymentContainer>
              <PaymentAccept height={30} />
            </PaymentContainer>
          </OrderSummary>
        </CheckoutContent>
      </Container>
    </MainContainer>
  );
};

const PaymentNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #f0f9ff;
  border: 1px solid #b3e0ff;
  border-radius: 8px;
  padding: 15px;
  margin-top: 15px;

  .icon {
    color: #0088cc;
    font-size: 20px;
    flex-shrink: 0;
  }

  p {
    margin: 0;
    color: #333;
    font-size: 14px;
    line-height: 1.5;
  }
`;

export default CheckoutPage;
